import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import CloudIcon from "@mui/icons-material/Cloud";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PaymentsIcon from "@mui/icons-material/Payments";
import BalanceIcon from "@mui/icons-material/Balance";

import { GetCountryConversionInsightsCall } from "../../redux/api/report.api";
import {
  countryDisplayName,
  isoToFlag,
  isUnknownCountry,
} from "./countryUtils";

const CLOUD_COLORS = [
  "#7c3aed",
  "#2563eb",
  "#059669",
  "#d97706",
  "#db2777",
  "#0891b2",
  "#4f46e5",
  "#16a34a",
  "#ea580c",
  "#be185d",
];

function weightForMode(row, mode) {
  const registrations = Number(row.registrations || 0);
  const purchases = Number(row.purchases || 0);
  if (mode === "registrations") return registrations;
  if (mode === "purchases") return purchases;
  return registrations + purchases * 2;
}

function fontSizeForValue(value, min, max) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (max <= min) return 22;
  const t = (value - min) / (max - min);
  return Math.round(13 + t * 34);
}

export default function AdminStatsCountryWordCloud() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [mode, setMode] = useState("combined");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await GetCountryConversionInsightsCall({
        page: 1,
        limit: 100,
        sort: "registrations",
        order: "desc",
        filter: "known",
      });
      if (cancelled) return;
      if (res && Array.isArray(res.items)) {
        setItems(res.items);
        setTotals(res.totals || null);
      } else {
        setItems([]);
        setTotals(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cloudItems = useMemo(() => {
    const weighted = items
      .filter((row) => !isUnknownCountry(row.country))
      .map((row, index) => {
        const weight = weightForMode(row, mode);
        const label = countryDisplayName(row.country);
        return {
          country: row.country,
          label,
          flag: isoToFlag(row.country),
          registrations: Number(row.registrations || 0),
          purchases: Number(row.purchases || 0),
          weight,
          color: CLOUD_COLORS[index % CLOUD_COLORS.length],
        };
      })
      .filter((row) => row.weight > 0);

    if (!weighted.length) return [];

    const weights = weighted.map((r) => r.weight);
    const min = Math.min(...weights);
    const max = Math.max(...weights);

    return weighted
      .map((row) => ({
        ...row,
        fontSize: fontSizeForValue(row.weight, min, max),
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [items, mode]);

  const modeHint =
    mode === "registrations"
      ? "Bigger = more registrations"
      : mode === "purchases"
        ? "Bigger = more active subscriptions"
        : "Bigger = registrations + 2× subscriptions";

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2.5,
        borderRadius: 3,
        borderColor: alpha(theme.palette.primary.main, 0.22),
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.95,
              )} 55%)`
            : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.14)} 0%, ${theme.palette.background.paper} 60%)`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <CloudIcon color="primary" />
              <Typography variant="h5" fontWeight={800}>
                Country reach
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Word size reflects volume — switch between registrations, subscriptions, or
              both combined.
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_e, next) => next && setMode(next)}
            sx={{
              flexWrap: "wrap",
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              borderRadius: 2,
              p: 0.5,
            }}
          >
            <ToggleButton value="combined">
              <BalanceIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Combined
            </ToggleButton>
            <ToggleButton value="registrations">
              <HowToRegIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Registrations
            </ToggleButton>
            <ToggleButton value="purchases">
              <PaymentsIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Subscriptions
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`${totals?.countries ?? cloudItems.length} countries`}
          />
          <Chip
            size="small"
            variant="outlined"
            icon={<HowToRegIcon />}
            label={`${(totals?.registrations ?? 0).toLocaleString()} registrations`}
          />
          <Chip
            size="small"
            variant="outlined"
            color="success"
            icon={<PaymentsIcon />}
            label={`${(totals?.purchases ?? 0).toLocaleString()} subscriptions`}
          />
          <Chip size="small" label={modeHint} sx={{ fontWeight: 500 }} />
        </Stack>

        {loading ? (
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
        ) : cloudItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
            No country data yet.
          </Typography>
        ) : (
          <Box
            sx={{
              minHeight: 260,
              py: 2,
              px: { xs: 1, sm: 2 },
              borderRadius: 2.5,
              bgcolor: alpha(
                theme.palette.background.paper,
                theme.palette.mode === "dark" ? 0.35 : 0.72,
              ),
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
              display: "flex",
              flexWrap: "wrap",
              alignContent: "center",
              justifyContent: "center",
              gap: { xs: 0.75, sm: 1.25 },
              lineHeight: 1.1,
            }}
          >
            {cloudItems.map((row) => (
              <Tooltip
                key={row.country}
                title={`${row.flag ? `${row.flag} ` : ""}${row.label} — ${row.registrations.toLocaleString()} registrations · ${row.purchases.toLocaleString()} subscriptions`}
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: row.fontSize,
                    fontWeight: 700,
                    color: row.color,
                    px: 0.4,
                    cursor: "default",
                    transition: "transform 0.15s ease, opacity 0.15s ease",
                    "&:hover": {
                      transform: "scale(1.06)",
                      opacity: 0.92,
                    },
                  }}
                >
                  {row.label}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
